// ─────────────────────────────────────────
// heroMedia.ts — Filme do painel do hero (home)
// Tauvior — Hardware, sistemas embarcados e conectividade industrial
// ─────────────────────────────────────────
//
// O <video> nasce sem `src` e sem autoplay: todo mundo vê o poster, que é
// leve. O filme só é carregado e tocado em tela larga, sem pedido de
// movimento reduzido e sem economia de dados — no celular o poster basta e
// o visitante não paga megabytes por um enfeite.

const WIDE_QUERY    = '(min-width: 960px)';
const REDUCED_QUERY = '(prefers-reduced-motion: reduce)';

interface NetworkInformationLike {
  saveData?: boolean;
}

function prefersLightweight(): boolean {
  const connection = (navigator as Navigator & { connection?: NetworkInformationLike }).connection;
  return Boolean(connection?.saveData);
}

function canPlay(): boolean {
  return (
    window.matchMedia(WIDE_QUERY).matches &&
    !window.matchMedia(REDUCED_QUERY).matches &&
    !prefersLightweight() &&
    document.querySelector('[data-hero-video]')?.closest('[data-motion-paused]')?.getAttribute('data-motion-paused') !== 'true' &&
    document.querySelector('[data-hero-video]')?.closest('[data-motion-visible]')?.getAttribute('data-motion-visible') !== 'false'
  );
}

function start(video: HTMLVideoElement): void {
  const src = video.dataset.src;
  if (!src || video.dataset.loaded === 'true') return;

  // `play()` logo depois de trocar o src é interrompido pelo próprio load;
  // com autoplay (mudo) o navegador começa sozinho quando há dados.
  // Autoplay bloqueado: o poster continua no lugar, sem erro para o visitante.
  video.muted = true;
  video.autoplay = true;
  video.src = src;
  video.dataset.loaded = 'true';

  // Política mais restrita (alguns webviews): o autoplay não dispara. Uma
  // tentativa explícita quando há dados e, se ainda assim for bloqueada, o
  // filme começa na primeira interação do visitante.
  video.addEventListener('loadeddata', () => {
    if (!video.paused || !canPlay()) return;
    video.play().catch(() => {
      const resume = (): void => { if (canPlay()) void video.play().catch(() => undefined); };
      window.addEventListener('pointerdown', resume, { once: true });
      window.addEventListener('keydown', resume, { once: true });
    });
  }, { once: true });
}

export function initHeroMedia(): void {
  const video = document.querySelector<HTMLVideoElement>('[data-hero-video]');
  if (!video) return;

  if (canPlay()) start(video);

  // Quem gira o tablet ou aumenta a janela passa a ver o filme; o contrário
  // só pausa — o que já foi baixado não é descartado.
  const wide = window.matchMedia(WIDE_QUERY);
  wide.addEventListener('change', () => {
    if (canPlay()) {
      start(video);
      void video.play().catch(() => undefined);
    } else {
      video.pause();
    }
  });

  window.matchMedia(REDUCED_QUERY).addEventListener('change', (event) => {
    if (event.matches) video.pause();
    else requestAnimationFrame(() => { if (canPlay()) start(video); });
  });
}
